import { fmt, fmtDelta } from './format';
import {
    SEO_GROUP_TAIL,
    SEO_PATH_BLOCKS,
    academiaGroupLabel,
    authorGroupLabel,
    classifyLandingPath,
    collaboratorGroupLabel,
    licenseGroupLabel,
    seoBlockName,
} from './landingBlocks';
import type {
    SeoBlock,
    SeoCatalog,
    SeoCell,
    SeoChange,
    SeoGroup,
    SeoReport,
    SeoRow,
    SeoSeries,
    SeoWindow,
} from './types';

const FLOOR = 50;
const UNRELIABLE = 20;
const LABELS: Record<string, string> = {
    '/': 'Inicio',
    '/blog': 'Blog',
    '/atlas': 'Atlas',
    '/academia': 'Academia de pesca',
    '/licencias-pesca': 'Licencias de pesca',
    '/colaboradores': 'Colaboradores',
    '/?utm_source=google': 'Inicio desde la ficha de Google',
};

function num(value: number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function pos(value: number | null | undefined): number | null {
    if (value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function blogGroupLabel(path: string, types: Record<string, string>): string {
    if (path === '/blog') return 'Índice';
    const slug = decodeURIComponent(path.slice('/blog/'.length).split('/')[0] || '');
    if (!slug) return 'Índice';
    return types[slug] || 'Sin tipología';
}

export function storeGroupLabel(path: string, brands: Record<string, string>): string {
    if (path === '/tienda') return 'Índice';
    const product = /^\/tienda\/p\/([^/]+)/.exec(path);
    if (!product) return 'Otras páginas';
    const slug = decodeURIComponent(product[1]);
    return brands[slug] || 'Sin marca';
}

export function classifyPath(path: string): string | null {
    return classifyLandingPath(path);
}

function blockName(id: string | null): string {
    return seoBlockName(id);
}

function groupLabelForBlock(blockId: string, path: string, catalog: SeoCatalog | undefined): string | null {
    switch (blockId) {
        case 'blog':
            return catalog ? blogGroupLabel(path, catalog.blogTypes) : null;
        case 'tienda':
            return catalog ? storeGroupLabel(path, catalog.storeBrands) : null;
        case 'licencias':
            return licenseGroupLabel(path);
        case 'autores':
            return authorGroupLabel(path);
        case 'colaboradores':
            return collaboratorGroupLabel(path);
        case 'academia':
            return academiaGroupLabel(path);
        default:
            return null;
    }
}

function buildUnclassifiedHints(
    members: string[],
    weekIds: string[],
    byWeek: Record<string, Record<string, { impressions: number }>>,
): SeoReport['unclassified'] {
    const bySegment = new Map<string, { paths: string[]; impressions: number }>();
    for (const path of members) {
        const impressions = impressionsOf(path, weekIds, byWeek);
        if (impressions < FLOOR) continue;
        const segment = path.replace(/^\//, '').split('/')[0] || '';
        const key = segment || '(raíz)';
        const bucket = bySegment.get(key) ?? { paths: [], impressions: 0 };
        bucket.paths.push(path);
        bucket.impressions += impressions;
        bySegment.set(key, bucket);
    }
    return [...bySegment.entries()]
        .sort((a, b) => b[1].impressions - a[1].impressions)
        .map(([segment, bucket]) => {
            const sample = bucket.paths.sort()[0] ?? '/';
            const prefix = segment === '(raíz)' ? '' : segment;
            return {
                path: sample,
                label: prefix ? humanizeSegment(prefix) : 'Raíz',
                suggestion: prefix ? `Revisar /${prefix}/` : 'Revisar',
                url_count: bucket.paths.length,
                impressions: bucket.impressions,
            };
        });
}

function humanizeSegment(segment: string): string {
    const text = segment.replace(/[-_]/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
}

export function labelForPath(path: string): string {
    if (LABELS[path]) return LABELS[path];
    const slug = path.replace(/\/+$/, '').split('/').pop() || 'Inicio';
    const text = slug.replace(/[-_]/g, ' ').trim();
    if (!text) return 'Inicio';
    return text.charAt(0).toUpperCase() + text.slice(1);
}

interface Acc {
    clicks: number;
    impressions: number;
    posW: number;
}

function blank(): Acc {
    return { clicks: 0, impressions: 0, posW: 0 };
}

function add(acc: Acc, clicks: number, impressions: number, position: number | null): void {
    acc.clicks += clicks;
    acc.impressions += impressions;
    if (impressions && position != null) acc.posW += position * impressions;
}

function finish(acc: Acc): { clicks: number; impressions: number; position: number | null } {
    return {
        clicks: acc.clicks,
        impressions: acc.impressions,
        position: acc.impressions ? Math.round((acc.posW / acc.impressions) * 100) / 100 : null,
    };
}

function cellOf(clicks: number, impressions: number, position: number | null): SeoCell | null {
    if (!clicks && !impressions) return null;
    return {
        clicks,
        impressions,
        ctr: impressions ? Math.round((10000 * clicks) / impressions) / 100 : 0,
        position,
        unreliable: impressions < UNRELIABLE,
    };
}

function windowOf(items: { clicks: number; impressions: number; position: number | null }[]): SeoWindow {
    const acc = blank();
    for (const item of items) add(acc, item.clicks, item.impressions, item.position);
    const done = finish(acc);
    const weeks = items.length;
    return {
        ...done,
        weeks,
        clicks_week: weeks ? Math.round((done.clicks / weeks) * 100) / 100 : null,
        impressions_week: weeks ? Math.round((done.impressions / weeks) * 100) / 100 : null,
        unreliable: done.impressions < UNRELIABLE,
    };
}

function changeOf(before: SeoWindow | null, after: SeoWindow | null): SeoChange | null {
    if (!before || !after) return null;
    const sub = (left: number | null | undefined, right: number | null | undefined) => {
        if (left == null || right == null) return null;
        return Math.round((right - left) * 100) / 100;
    };
    return {
        clicks: sub(before.clicks_week, after.clicks_week),
        impressions: sub(before.impressions_week, after.impressions_week),
        position: sub(before.position, after.position),
    };
}

function card(
    id: string,
    label: string,
    before: number | null,
    after: number | null,
    decimals: number,
    hint: string,
) {
    const delta = before == null || after == null ? null : Math.round((after - before) * 100) / 100;
    return {
        id,
        label,
        before,
        after,
        delta,
        decimals,
        before_txt: fmt(before, decimals),
        after_txt: fmt(after, decimals),
        delta_txt: fmtDelta(delta, decimals),
        hint,
    };
}

export function buildSeoReport(series: SeoSeries, grain: 'week' | 'day' = 'week', catalog?: SeoCatalog): SeoReport {
    const weeks = series.weeks;
    const weekIds = weeks.map((week) => week.id);
    const span = grain === 'day' ? 7 : 4;
    const bucket = grain === 'day' ? 'día' : 'semana';
    const byWeek: Record<string, Record<string, { clicks: number; impressions: number; position: number | null }>> = {};
    for (const page of series.pages) {
        const bucket = byWeek[page.week_id] ?? (byWeek[page.week_id] = {});
        bucket[page.path] = {
            clicks: num(page.clicks),
            impressions: num(page.impressions),
            position: pos(page.position),
        };
    }

    const afterWeeks = weeks.slice(-span);
    const beforeWeeks = weeks.slice(-span * 2, -span);
    const afterIds = (afterWeeks.length >= 2 ? afterWeeks : weeks.slice(-1)).map((week) => week.id);
    const beforeIds = beforeWeeks.map((week) => week.id);
    const ready = afterWeeks.length >= 2;

    const paths = new Set<string>();
    for (const bucket of Object.values(byWeek)) {
        for (const path of Object.keys(bucket)) paths.add(path);
    }

    const grouped = new Map<string | null, string[]>();
    for (const path of paths) {
        const id = classifyPath(path);
        const list = grouped.get(id) ?? [];
        list.push(path);
        grouped.set(id, list);
    }

    const order: (string | null)[] = [...SEO_PATH_BLOCKS.map((rule) => rule.id), null];
    const blocks: SeoBlock[] = [];
    let loose: SeoReport['unclassified'] = [];

    for (const blockId of order) {
        const members = grouped.get(blockId) ?? [];
        if (!members.length) continue;
        const rows = rowsForMembers(members, weekIds, beforeIds, afterIds, byWeek);
        const groups = groupsFor(blockId, members, weekIds, beforeIds, afterIds, byWeek, catalog);
        if (blockId == null) {
            loose = buildUnclassifiedHints(members, weekIds, byWeek);
        }
        blocks.push({
            id: blockId ?? 'sin-clasificar',
            name: blockName(blockId),
            hidden: members.length - rows.filter((row) => !row.resto).length,
            rows,
            groups,
            summary: summaryRow(rows, weekIds.length),
        });
    }

    const pageWindow = (ids: string[]) => {
        const items = ids.map((id) => {
            const acc = blank();
            for (const item of Object.values(byWeek[id] ?? {})) add(acc, item.clicks, item.impressions, item.position);
            return finish(acc);
        });
        return windowOf(items);
    };
    const beforePages = pageWindow(beforeIds);
    const afterPages = pageWindow(afterIds);
    const ctr = (bucket: SeoWindow) => (
        bucket.impressions ? Math.round((10000 * num(bucket.clicks)) / num(bucket.impressions)) / 100 : null
    );

    const averageHint = grain === 'day' ? 'Media diaria de la suma de páginas' : 'Media semanal de la suma de páginas';
    const cards = [
        card('clicks', `Clics por ${bucket}`, beforePages.clicks_week ?? null, afterPages.clicks_week ?? null, 1, averageHint),
        card('position', 'Posición media', beforePages.position ?? null, afterPages.position ?? null, 1, 'Ponderada por impresiones. Bajar es mejorar.'),
        card('impressions', `Impresiones por ${bucket}`, beforePages.impressions_week ?? null, afterPages.impressions_week ?? null, 0, averageHint),
        card('ctr', 'CTR', ctr(beforePages), ctr(afterPages), 2, 'Clics entre impresiones de las páginas de la ventana, en puntos porcentuales.'),
    ];

    const movers = blocks.flatMap((block) => block.rows.filter((row) => !row.resto && row.change?.clicks != null).map((row) => ({
        label: row.label,
        path: row.path || '',
        delta: row.change?.clicks ?? 0,
        before: row.before?.clicks_week ?? 0,
        after: row.after?.clicks_week ?? 0,
    })));
    const ups = movers.filter((row) => row.delta > 0).sort((a, b) => b.delta - a.delta);
    const downs = movers.filter((row) => row.delta < 0).sort((a, b) => a.delta - b.delta);
    const clicksCard = cards[0];
    const lines = [
        `Los clics pasan de ${clicksCard.before_txt} a ${clicksCard.after_txt} por ${bucket} (${clicksCard.delta_txt}).`,
        ups[0]
            ? `La página que más sube es ${ups[0].label} (${ups[0].path}): ${fmtDelta(ups[0].delta, 1)} clics por ${bucket}, de ${fmt(ups[0].before, 1)} a ${fmt(ups[0].after, 1)}.`
            : 'Ninguna página sube por encima del ruido.',
        downs[0]
            ? `La página que más baja es ${downs[0].label} (${downs[0].path}): ${fmtDelta(downs[0].delta, 1)} clics por ${bucket}, de ${fmt(downs[0].before, 1)} a ${fmt(downs[0].after, 1)}.`
            : 'Ninguna página baja por encima del ruido.',
    ];

    const opportunities = buildOpportunities(series, byWeek, weekIds, grain);
    if (opportunities.edge[0]) {
        const edge = opportunities.edge[0];
        lines.push(`Primera oportunidad: «${edge.query}» en ${edge.label} (${edge.path}), posición ${fmt(edge.position, 1)} y ${fmt(edge.impressions, 0)} impresiones.`);
    } else {
        lines.push('No hay consultas repetidas en el borde de la primera página.');
    }
    lines.push(
        series.data_through
            ? `Último día con datos: ${series.data_through}. Incluye lo que Search Console ya ha publicado, aunque el tramo reciente todavía se mueva.`
            : 'Aún no hay fecha de cierre.',
    );

    return {
        grain,
        site: 'aipeces',
        name: 'AiPeces',
        property: 'sc-domain:aipeces.com',
        colors: { navy: '#0A3A4A', cyan: '#3DCFC2', petrol: '#0E7490' },
        data_through: series.data_through ?? '',
        generated_at: series.finished_at ?? '',
        period: {
            start: weeks[0]?.start ?? '',
            end: weeks[weeks.length - 1]?.end ?? '',
        },
        compare: {
            ready,
            consolidated: afterWeeks.length >= span,
            before_label: grain === 'day' ? 'Siete días anteriores' : 'Cuatro semanas anteriores',
            after_label: grain === 'day'
                ? (ready ? 'Últimos siete días' : 'Último día')
                : (ready ? 'Últimas cuatro semanas' : 'Última semana'),
            before_weeks: beforeIds.length,
            after_weeks: afterIds.length,
        },
        weeks: weeks.map((week) => ({ id: week.id, start: String(week.start).slice(0, 10), end: String(week.end).slice(0, 10), hitos: [] })),
        cards,
        season: weeks.map((week) => ({
            id: week.id,
            clicks: num(week.clicks),
            position: pos(week.position),
            yoy_clicks: week.yoy_clicks == null ? null : num(week.yoy_clicks),
            yoy_position: pos(week.yoy_position),
        })),
        launch_index: null,
        blocks,
        unclassified: loose,
        opportunities,
        findings: { worked: lines.slice(0, 2), flat: lines.slice(2, 3), care: lines.slice(3) },
        lines,
        reconciliation: weekIds.map((id) => {
            const pageClicks = Object.values(byWeek[id] ?? {}).reduce((sum, item) => sum + item.clicks, 0);
            const site = weeks.find((week) => week.id === id);
            const siteClicks = num(site?.clicks);
            return { week: id, page_clicks: pageClicks, site_clicks: siteClicks, diff: pageClicks - siteClicks };
        }),
        urls_per_week: Object.fromEntries(weekIds.map((id) => [id, Object.keys(byWeek[id] ?? {}).length])),
        empty_weeks: weekIds.filter((id) => !Object.keys(byWeek[id] ?? {}).length),
        truncated: Boolean(series.truncated),
        queries_truncated: Boolean(series.queries_truncated),
        impression_floor: FLOOR,
    };
}

function rowsForMembers(
    members: string[],
    weekIds: string[],
    beforeIds: string[],
    afterIds: string[],
    byWeek: Record<string, Record<string, { clicks: number; impressions: number; position: number | null }>>,
    restoLabel = 'Resto del bloque',
): SeoRow[] {
    const ranked = [...members].sort((a, b) => impressionsOf(b, weekIds, byWeek) - impressionsOf(a, weekIds, byWeek));
    const visible = ranked.filter((path) => impressionsOf(path, weekIds, byWeek) >= FLOOR);
    const hidden = ranked.filter((path) => impressionsOf(path, weekIds, byWeek) < FLOOR);
    const rows = visible.map((path) => rowFor(path, weekIds, beforeIds, afterIds, byWeek));
    if (hidden.length) rows.push(restoRow(hidden, weekIds, beforeIds, afterIds, byWeek, restoLabel));
    return rows;
}

function groupsFor(
    blockId: string | null,
    members: string[],
    weekIds: string[],
    beforeIds: string[],
    afterIds: string[],
    byWeek: Record<string, Record<string, { clicks: number; impressions: number; position: number | null }>>,
    catalog: SeoCatalog | undefined,
): SeoGroup[] | undefined {
    if (!blockId) return undefined;
    const buckets = new Map<string, string[]>();
    for (const path of members) {
        const label = groupLabelForBlock(blockId, path, catalog);
        if (label == null) return undefined;
        const list = buckets.get(label) ?? [];
        list.push(path);
        buckets.set(label, list);
    }
    if (buckets.size <= 1) return undefined;
    return [...buckets.entries()]
        .map(([label, paths]) => {
            const rows = rowsForMembers(paths, weekIds, beforeIds, afterIds, byWeek, 'Resto');
            return {
                id: label,
                label,
                rows,
                summary: summaryRow(rows, weekIds.length),
            };
        })
        .sort((a, b) => {
            const tailA = SEO_GROUP_TAIL.has(a.label) ? 1 : 0;
            const tailB = SEO_GROUP_TAIL.has(b.label) ? 1 : 0;
            if (tailA !== tailB) return tailA - tailB;
            if (a.label === 'Índice') return -1;
            if (b.label === 'Índice') return 1;
            return impressionsOfRows(b.rows) - impressionsOfRows(a.rows);
        });
}

function impressionsOfRows(rows: SeoRow[]): number {
    return rows.reduce((sum, row) => sum + row.cells.reduce((inner, cell) => inner + (cell?.impressions ?? 0), 0), 0);
}

function impressionsOf(
    path: string,
    weekIds: string[],
    byWeek: Record<string, Record<string, { impressions: number }>>,
): number {
    return weekIds.reduce((sum, id) => sum + (byWeek[id]?.[path]?.impressions ?? 0), 0);
}

function rowFor(
    path: string,
    weekIds: string[],
    beforeIds: string[],
    afterIds: string[],
    byWeek: Record<string, Record<string, { clicks: number; impressions: number; position: number | null }>>,
): SeoRow {
    const cells = weekIds.map((id) => {
        const item = byWeek[id]?.[path];
        return item ? cellOf(item.clicks, item.impressions, item.position) : null;
    });
    const before = windowOf(beforeIds.map((id) => byWeek[id]?.[path]).filter((item): item is NonNullable<typeof item> => Boolean(item && (item.impressions || item.clicks))));
    const after = windowOf(afterIds.map((id) => byWeek[id]?.[path]).filter((item): item is NonNullable<typeof item> => Boolean(item && (item.impressions || item.clicks))));
    const beforeWindow = before.weeks ? before : null;
    const afterWindow = after.weeks ? after : null;
    return {
        path,
        label: labelForPath(path),
        tag: null,
        resto: false,
        resto_count: 0,
        cells,
        before: beforeWindow,
        after: afterWindow,
        change: changeOf(beforeWindow, afterWindow),
    };
}

function restoRow(
    hidden: string[],
    weekIds: string[],
    beforeIds: string[],
    afterIds: string[],
    byWeek: Record<string, Record<string, { clicks: number; impressions: number; position: number | null }>>,
    label = 'Resto del bloque',
): SeoRow {
    const fold = (ids: string[]) => ids.map((id) => {
        const acc = blank();
        for (const path of hidden) {
            const item = byWeek[id]?.[path];
            if (item) add(acc, item.clicks, item.impressions, item.position);
        }
        return finish(acc);
    });
    const cells = fold(weekIds).map((item) => cellOf(item.clicks, item.impressions, item.position));
    const before = windowOf(fold(beforeIds));
    const after = windowOf(fold(afterIds));
    return {
        path: '',
        label,
        resto: true,
        resto_count: hidden.length,
        cells,
        before,
        after,
        change: changeOf(before, after),
    };
}

function summaryRow(rows: SeoRow[], weekCount: number): SeoRow {
    const cells = Array.from({ length: weekCount }, (_, index) => {
        const acc = blank();
        for (const row of rows) {
            const cell = row.cells[index];
            if (cell) add(acc, cell.clicks, cell.impressions, cell.position);
        }
        const done = finish(acc);
        return cellOf(done.clicks, done.impressions, done.position);
    });
    const foldWindow = (key: 'before' | 'after') => {
        const acc = blank();
        let clicksWeek = 0;
        let impressionsWeek = 0;
        for (const row of rows) {
            const bucket = row[key];
            if (!bucket) continue;
            add(acc, num(bucket.clicks), num(bucket.impressions), pos(bucket.position));
            clicksWeek += num(bucket.clicks_week);
            impressionsWeek += num(bucket.impressions_week);
        }
        const done = finish(acc);
        return {
            ...done,
            clicks_week: Math.round(clicksWeek * 100) / 100,
            impressions_week: Math.round(impressionsWeek * 100) / 100,
            unreliable: done.impressions < UNRELIABLE,
        } satisfies SeoWindow;
    };
    const before = foldWindow('before');
    const after = foldWindow('after');
    return {
        label: '',
        cells,
        before,
        after,
        change: changeOf(before, after),
    };
}

function buildOpportunities(
    series: SeoSeries,
    byWeek: Record<string, Record<string, { clicks: number; impressions: number; position: number | null }>>,
    weekIds: string[],
    grain: 'week' | 'day',
): SeoReport['opportunities'] {
    const byQuery = new Map<string, { week: string; impressions: number; pages: { path: string; label: string; impressions: number }[] }[]>();
    const byWeekQuery = new Map<string, Map<string, { path: string; label: string; clicks: number; impressions: number; position: number | null }[]>>();
    for (const row of series.queries) {
        const query = row.query.trim();
        if (!query) continue;
        const weekMap = byWeekQuery.get(row.week_id) ?? new Map();
        const list = weekMap.get(query) ?? [];
        list.push({
            path: row.path,
            label: labelForPath(row.path),
            clicks: num(row.clicks),
            impressions: num(row.impressions),
            position: pos(row.position),
        });
        weekMap.set(query, list);
        byWeekQuery.set(row.week_id, weekMap);
    }
    for (const [week, queries] of byWeekQuery) {
        for (const [query, pages] of queries) {
            if (pages.filter((page) => page.impressions > 0).length < 2) continue;
            const hits = byQuery.get(query) ?? [];
            hits.push({
                week,
                impressions: pages.reduce((sum, page) => sum + page.impressions, 0),
                pages: pages.map((page) => ({ path: page.path, label: page.label, impressions: page.impressions })),
            });
            byQuery.set(query, hits);
        }
    }
    const cannibal = [...byQuery.entries()].map(([query, hits]) => {
        const latest = [...hits].sort((a, b) => a.week.localeCompare(b.week)).at(-1)!;
        return { query, week: latest.week, impressions: latest.impressions, pages: latest.pages, weeks: hits.length };
    }).sort((a, b) => b.impressions - a.impressions).slice(0, 8);

    const edgeAcc = new Map<string, { query: string; path: string; weeks: Set<string>; clicks: number; impressions: number; posW: number }>();
    for (const row of series.queries) {
        const position = pos(row.position);
        if (position == null || position < 8 || position > 15) continue;
        const query = row.query.trim();
        if (!query) continue;
        const key = `${query}\u0000${row.path}`;
        const acc = edgeAcc.get(key) ?? { query, path: row.path, weeks: new Set<string>(), clicks: 0, impressions: 0, posW: 0 };
        acc.weeks.add(row.week_id);
        acc.clicks += num(row.clicks);
        const impressions = num(row.impressions);
        acc.impressions += impressions;
        acc.posW += position * impressions;
        edgeAcc.set(key, acc);
    }
    const edge = [...edgeAcc.values()]
        .filter((item) => item.weeks.size >= 2 && item.impressions >= 20)
        .map((item) => ({
            query: item.query,
            path: item.path,
            label: labelForPath(item.path),
            weeks: item.weeks.size,
            clicks: item.clicks,
            impressions: item.impressions,
            position: item.impressions ? Math.round((item.posW / item.impressions) * 100) / 100 : 0,
        }))
        .sort((a, b) => b.impressions - a.impressions)
        .slice(0, 8);

    const zero = [...new Set(Object.values(byWeek).flatMap((bucket) => Object.keys(bucket)))]
        .map((path) => {
            let impressions = 0;
            let quiet = 0;
            for (const id of weekIds) {
                const item = byWeek[id]?.[path];
                if (!item?.impressions) continue;
                impressions += item.impressions;
                if (item.clicks === 0) quiet += 1;
            }
            return { path, label: labelForPath(path), weeks: quiet, impressions };
        })
        .filter((item) => item.weeks >= (grain === 'day' ? 7 : 4) && item.impressions > 0)
        .sort((a, b) => b.impressions - a.impressions)
        .slice(0, 8);

    return { edge, zero_clicks: zero, cannibal };
}
